import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate, useLocation } from "react-router"

import "./index.scss"

import Button from "react-bootstrap/Button"
// import { UpdateIndicator } from "components"
import { FiArrowLeft, FiHome, FiLogOut, FiCopy } from "react-icons/fi"
import { BsFillChatDotsFill } from "react-icons/bs"
import { FaKey } from "react-icons/fa"

import { auth, message, repositories, token, visual, librarian } from "state_management"
import useT from "utils/hooks/useT"
import { HelpButton, Popup } from "components"
import { Title, Container, Text } from "components/Form"

const VARIANT_OUTLINE_PRIMARY = "outline-primary"

// Buttons used on various schedule screens.
export default function ScheduleButtonsAdmin() {
  const dispatch = useDispatch()
  const { t } = useT("messages")
  const { t: tc } = useT("common")
  const location = useLocation()
  const navigate = useNavigate()
  const role = useSelector((state) => state.auth.role)
  const isCustomerAdmin = role === "customer_admin"
  const hasApiToken = useSelector((state) => state.auth.hasApiToken)
  const isFrontPage = isCustomerAdmin ? location.pathname === "/customers" : location.pathname === "/locations"
  const [activePopupApi, setactivePopupApi] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const [showToken, setShowToken] = useState(false)
  const tokenString = useSelector((state) => state.token.token)
  const [currentTab, setCurrentTab] = useState(1)

  const config = useSelector((state) => state.librarian.config)

  const [promptForm, setPromptForm] = useState({
    generalPrefix: null,
    relevantDocumentation: null,
    citationGuidelinesPrompt: null,
    conversationPrompt: null,
    currentQuestion: null,
    descriptionPrompt: null,
    filePathPrompt: null,
    topDescriptionPrompt: null,
    optimizedQuestionPrompt: null,
  })
  const updatePromptField = (field, value) => setPromptForm(prev => ({ ...prev, [field]: value }))

  // SPEC-056 UI-review (#4): the chatbot-prompt config is fetched when the PANEL opens, not at
  // screen mount — a failing config service (500) used to toast "An unexpected error occurred"
  // right at customer-admin login and leave an unhandled rejection. A failure now surfaces as
  // a translated toast when the panel is actually used (handleSubmit guards on `config`, so a
  // failed fetch can never cause saving blank prompts).
  useEffect(() => {
    if (isCustomerAdmin && showPrompt) {
      dispatch(librarian.fetchConfig()).catch(() => {})
    }
  }, [isCustomerAdmin, showPrompt, dispatch])

  useEffect(() => {
    if (config) {
      setPromptForm({
        generalPrefix: config.generalPrefix,
        relevantDocumentation: config.relevantDocumentation,
        citationGuidelinesPrompt: config.citationGuidelinesPrompt,
        conversationPrompt: config.conversationPrompt,
        currentQuestion: config.currentQuestion,
        descriptionPrompt: config.descriptionPrompt,
        filePathPrompt: config.filePathPrompt,
        topDescriptionPrompt: config.topDescriptionPrompt,
        optimizedQuestionPrompt: config.optimizedQuestionPrompt,
      })
    }
  }, [config])

  const handleGoBack = () => {
    updateRepository()
    navigate(-1)
  }

  const handleCopy = () => {
    navigator.clipboard
      .writeText(tokenString)
      .then(() => {
        dispatch(message.success(t("adhoc.copiedToClipboard")))
      })
      .catch(() => {
        dispatch(message.error(t("adhoc.couldNotCopyToClipboard")))
      })
  }

  const handleSubmit = () => {
    // Never diff against (or overwrite with) nothing: if the config fetch failed, the form
    // holds empty defaults and saving would blank the real prompts on the config service.
    if (!config) {
      dispatch(message.warning(t("error.librarian/fetchConfig/ERROR")))
      return
    }
    if (promptForm.generalPrefix !== config.generalPrefix) {
      dispatch(librarian.setGeneralPrefix({ prefix: promptForm.generalPrefix }))
    }
    if (promptForm.relevantDocumentation !== config.relevantDocumentation) {
      dispatch(librarian.setRelevantDocumentation({ string: promptForm.relevantDocumentation }))
    }
    if (promptForm.citationGuidelinesPrompt !== config.citationGuidelinesPrompt) {
      dispatch(librarian.setCitationGuidelinesPrompt({ string: promptForm.citationGuidelinesPrompt }))
    }
    if (promptForm.conversationPrompt !== config.conversationPrompt) {
      dispatch(librarian.setConversationPrompt({ string: promptForm.conversationPrompt }))
    }
    if (promptForm.currentQuestion !== config.currentQuestion) {
      dispatch(librarian.setCurrentQuestion({ string: promptForm.currentQuestion }))
    }
    if (promptForm.descriptionPrompt !== config.descriptionPrompt) {
      dispatch(librarian.setDescriptionPrompt({ string: promptForm.descriptionPrompt }))
    }
    if (promptForm.filePathPrompt !== config.filePathPrompt) {
      dispatch(librarian.setFilePathPrompt({ string: promptForm.filePathPrompt }))
    }
    if (promptForm.topDescriptionPrompt !== config.topDescriptionPrompt) {
      dispatch(librarian.setTopDescriptionPrompt({ string: promptForm.topDescriptionPrompt }))
    }
    if (promptForm.optimizedQuestionPrompt !== config.optimizedQuestionPrompt) {
      dispatch(librarian.setOptimizedQuestionPrompt({ string: promptForm.optimizedQuestionPrompt }))
    }
    setShowPrompt(false)
  }

  const updateRepository = () => {
    const isRepositoryView =
      location.pathname.includes("/customers/global-repositories/") ||
      location.pathname.includes("/locations/repository/")
    if (isRepositoryView) {
      const repositoryId = location.pathname.split("/")[3]
      dispatch(repositories.fetchRepository(repositoryId))
    }
  }

  return (
    <>
      {showPrompt && isCustomerAdmin && currentTab === 1 && (
        <Popup
          onCancel={() => {
            setShowPrompt(false)
          }}
          title={tc("chatbotSystemPrompt")}
        >
          <div className="tab-buttons">
            <Button
              variant={currentTab === 1 ? "primary" : VARIANT_OUTLINE_PRIMARY}
              onClick={() => setCurrentTab(1)}
              size="sm"
            >
              1
            </Button>
            <Button
              variant={currentTab === 2 ? "primary" : VARIANT_OUTLINE_PRIMARY}
              onClick={() => setCurrentTab(2)}
              size="sm"
              style={{ marginLeft: "0.5em" }}
            >
              2
            </Button>
          </div>
          <Container>
            <Title>{tc("generalPrefix")}</Title>
            <Text
              value={promptForm.generalPrefix}
              large={true}
              required
              onChange={(value) => updatePromptField("generalPrefix", value)}
              rows={5}
            />
          </Container>

          <span style={{ display: 'block', marginBottom: '1em' }}>
            {tc("chatbotPrefix")}
          </span>

          <Container>
            <Title>{tc("relevantDocumentation")}</Title>
            <Text
              value={promptForm.relevantDocumentation}
              large={true}
              required
              onChange={(value) => updatePromptField("relevantDocumentation", value)}
              rows={5}
            />
          </Container>

          <Container>
            <Title>{tc("citationGuidelines")}</Title>
            <Text
              value={promptForm.citationGuidelinesPrompt}
              large={true}
              required
              onChange={(value) => updatePromptField("citationGuidelinesPrompt", value)}
              rows={5}
            />
          </Container>

          <span style={{ display: 'block', marginBottom: '1em' }}>
            {tc("repositoryPrefix")}
          </span>

          <Container>
            <Title>{tc("previousConversation")}</Title>
            <Text
              value={promptForm.conversationPrompt}
              large={true}
              required
              onChange={(value) => updatePromptField("conversationPrompt", value)}
              rows={5}
            />
          </Container>
          <Container>
            <Title>{tc("currentQuestion")}</Title>
            <Text
              value={promptForm.currentQuestion}
              large={true}
              required
              onChange={(value) => updatePromptField("currentQuestion", value)}
              rows={5}
            />
          </Container>
          <Button variant="primary" onClick={handleSubmit}>
            {tc("save")}
          </Button>
          <Button
            style={{ marginLeft: "0.75em" }}
            variant="danger"
            onClick={() => {
              setPromptForm(prev => ({
                ...prev,
                generalPrefix: config.generalPrefix,
                relevantDocumentation: config.relevantDocumentation,
                citationGuidelinesPrompt: config.citationGuidelinesPrompt,
                conversationPrompt: config.conversationPrompt,
                currentQuestion: config.currentQuestion,
              }))
              setShowPrompt(false)
            }}
          >
            {tc("cancel")}
          </Button>
        </Popup>
      )}
      {activePopupApi && isCustomerAdmin && (
        <Popup
          onCancel={() => {
            if (!showToken) {
              setactivePopupApi(false)
            }
          }}
          title={tc("customerAdminApiKeyForGlobalRepositories")}
        >
          {hasApiToken ? (
            <Button
              className="token-button"
              variant="danger"
              onClick={() => {
                dispatch(token.deleteCustomerAdminToken())
                dispatch(auth.setHasAPIToken(false))
              }}
            >
              {tc("deleteApiKey")}
            </Button>
          ) : (
            <Button
              className="token-button"
              variant="success"
              onClick={() => {
                dispatch(token.createCustomerAdminToken())
                dispatch(auth.setHasAPIToken(true))
                setShowToken(true)
              }}
            >
              {tc("createApiKey")}
            </Button>
          )}
        </Popup>
      )}
      {showPrompt && isCustomerAdmin && currentTab === 2 && (
        <Popup
          onCancel={() => {
            setShowPrompt(false)
          }}
          title={tc("chatbotSystemPrompt")}
        >
          <div className="tab-buttons">
            <Button
              variant={currentTab === 1 ? "primary" : VARIANT_OUTLINE_PRIMARY}
              onClick={() => setCurrentTab(1)}
              size="sm"
            >
              1
            </Button>
            <Button
              variant={currentTab === 2 ? "primary" : VARIANT_OUTLINE_PRIMARY}
              onClick={() => setCurrentTab(2)}
              size="sm"
              style={{ marginLeft: "0.5em" }}
            >
              2
            </Button>
          </div>
          <Container>
            <Title>{tc("descriptionPrompt")}</Title>
            <Text
              value={promptForm.descriptionPrompt}
              large={true}
              required
              onChange={(value) => updatePromptField("descriptionPrompt", value)}
              rows={5}
            />
          </Container>
          <Container>
            <Title>{tc("filePathPrompt")}</Title>
            <Text
              value={promptForm.filePathPrompt}
              large={true}
              required
              onChange={(value) => updatePromptField("filePathPrompt", value)}
              rows={5}
            />
          </Container>
          <Container>
            <Title>{tc("topDescriptionPrompt")}</Title>
            <Text
              value={promptForm.topDescriptionPrompt}
              large={true}
              required
              onChange={(value) => updatePromptField("topDescriptionPrompt", value)}
              rows={5}
            />
          </Container>
          <Container>
            <Title>{tc("optimizedQuestionPrompt")}</Title>
            <Text
              value={promptForm.optimizedQuestionPrompt}
              large={true}
              required
              onChange={(value) => updatePromptField("optimizedQuestionPrompt", value)}
              rows={5}
            />
          </Container>
          <Button variant="primary" onClick={handleSubmit}>
            {tc("save")}
          </Button>
          <Button
            style={{ marginLeft: "0.75em" }}
            variant="danger"
            onClick={() => {
              setPromptForm(prev => ({
                ...prev,
                descriptionPrompt: config.descriptionPrompt,
                filePathPrompt: config.filePathPrompt,
                topDescriptionPrompt: config.topDescriptionPrompt,
              }))
              setShowPrompt(false)
            }}
          >
            {tc("cancel")}
          </Button>
        </Popup>
      )}

      {/* Main Buttons Container */}
      <div className="ScheduleButtons admin">
        {/* Left Menu Buttons */}
        <div className="leftmenu-button" style={{ visibility: !isFrontPage ? "visible" : "hidden" }}>
          <Button variant="primary" title={tc("goBack")} onClick={handleGoBack}>
            <FiArrowLeft />
          </Button>
        </div>

        {/* Center Buttons */}
        <div className="center-buttons">
          <Button
            variant="primary"
            title={tc("home")}
            onClick={() => {
              updateRepository()
              navigate(`/${isCustomerAdmin ? "customers" : "locations"}`)
            }}
          >
            <FiHome />
          </Button>
          {isCustomerAdmin && isFrontPage && (
            <div className="globalmessage-button">
              <Button
                variant="primary"
                title={tc("editGlobalMessage")}
                onClick={() => {
                  dispatch(visual.showGlobalMessageConfig(true))
                }}
              >
                <BsFillChatDotsFill />
              </Button>
            </div>
          )}
          {isCustomerAdmin && (
            <Button
              variant="primary"
              title={tc("apiKey")}
              onClick={() => {
                setactivePopupApi(true)
              }}
            >
              <FaKey />
            </Button>
          )}
          {isCustomerAdmin && isFrontPage && (
            <div className="globalmessage-button">
              <Button
                variant="primary"
                title={tc("editSystemPrompts")}
                onClick={() => {
                  setShowPrompt(true)
                }}
              >
                {tc("chatbotSystemPrompts")}
              </Button>
            </div>
          )}
        </div>

        {/* Right Menu Buttons */}
        <div className="rightmenu-button">
          <HelpButton area="admin" />
          <Button
            variant="primary"
            onClick={() => dispatch(auth.logout()).then(() => navigate("/login"))}
            title={tc("logout")}
          >
            <FiLogOut />
          </Button>
        </div>
      </div>

      {/* Token Popup */}
      {showToken && (
        <Popup
          nonScrollable
          className="tokenPopupContainer"
          onCancel={() => {
            dispatch(token.hideToken()) // Delete the token from redux
            setShowToken(false)
            setactivePopupApi(false)
          }}
        >
          <div>
            <h4>{tc("permanentApiToken")}</h4>
            <p>
              {tc("permanentApiTokenDescription")}
            </p>
            <Container>
              <Title>{tc("apiToken")}</Title>
              <Title>
                {tokenString}{" "}
                <span title={tc("copyToClipboard")} className="teamconfig-copy-clipboard">
                  <FiCopy onClick={handleCopy} />
                </span>
              </Title>
            </Container>
          </div>
          <Button
            className="close-popup-button"
            variant="primary"
            onClick={() => {
              dispatch(token.hideToken()) // Delete the token from redux
              setShowToken(false)
            }}
          >
            {tc("ok")}
          </Button>
        </Popup>
      )}
    </>
  )
}

